import React, { useEffect, useState, useMemo, Fragment } from 'react';
import {
  Skeleton,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Collapse,
  Link as MuiLink,
  Chip,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CategoryIcon from '@mui/icons-material/Category';
import { Link } from 'react-router-dom';
import DateRangeFilter from '../common/DateRangeFilter';
import { Report } from '../../models/report';

// Function to normalize Vietnamese text
const normalizeVietnamese = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

// Added helper function to format dates for display
const formatProcessedDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const OrderItemsReport = (props) => {
  const { setPageTitle } = props; // Destructure for useEffect dependency
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [expandedRows, setExpandedRows] = useState({}); // State for expanded rows

  const handleExpandClick = (productId) => {
    setExpandedRows(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  useEffect(() => {
    const fetchReport = async () => {
      if (!startDate || !endDate) {
        setReport(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        setPageTitle('Báo cáo đơn hàng');

        const startIso = `${startDate}T00:00:00+07:00`;
        const endIso = `${endDate}T23:59:59+07:00`;

        const response = await Report.fetch('order_items', {
          start_date: startIso,
          end_date: endIso
        });
        setReport(response); 
      } catch (error) {
        console.error('Error fetching report:', error);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, setPageTitle]);

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!report?.products) return [];
    const normalizedQuery = normalizeVietnamese(searchQuery);
    return report.products.filter(product =>
      normalizeVietnamese(product.product_name).includes(normalizedQuery)
    );
  }, [report?.products, searchQuery]);

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={600} />;
  }

  if (!report || !report.products || report.products.length === 0) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '24px auto' }}>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            mt: 2
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {!startDate || !endDate
              ? 'Vui lòng chọn khoảng thời gian để xem báo cáo'
              : 'Không tìm thấy dữ liệu báo cáo cho khoảng thời gian đã chọn.'
            }
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '24px auto', pl: 3, pr: 3 }}>
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        sx={{ mb: 3 }}
      />

      <TextField
        fullWidth
        label="Tìm kiếm sản phẩm"
        variant="outlined"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 2,
          "& .MuiTableCell-head": {
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold"
          }
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="order items report table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40px' }} /> {/* For expand button */}
              <TableCell>STT</TableCell>
              <TableCell>Tên sản phẩm</TableCell>
              {/* <TableCell align="right">Tồn cuối hôm qua</TableCell>
              <TableCell align="right">Điều chỉnh KT hôm nay</TableCell>
              <TableCell align="right">PO nhận hôm nay</TableCell> */}
              <TableCell align="right">SL đầu ngày</TableCell>
              <TableCell align="right">Tổng SL bán</TableCell>
              <TableCell align="right">Tồn kho hiện tại</TableCell>
              <TableCell align="right">Tổng Tiền Bán</TableCell>
              <TableCell align="right">Số đơn hàng</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product, index) => (
              <Fragment key={product.product_id}>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleExpandClick(product.product_id)}
                    >
                      {expandedRows[product.product_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell component="th" scope="row">
                    {product.product_name}
                  </TableCell>
                  {/* <TableCell align="right">{product.yesterday_ending || 0}</TableCell>
                  <TableCell align="right">{product.stocktake_adjustments_today || 0}</TableCell>
                  <TableCell align="right">{product.po_received_today || 0}</TableCell> */}
                  <TableCell align="right">{((product.yesterday_ending || 0) + (product.stocktake_adjustments_today || 0) + (product.po_received_today || 0)).toFixed(2)}</TableCell>
                  <TableCell align="right">{product.total_quantity_sold}</TableCell>
                  <TableCell align="right">{product.current_inventory || 0}</TableCell>
                  <TableCell align="right">
                    {product.total_amount_from_sales?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                  </TableCell>
                  <TableCell align="right">{product.number_of_orders}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={expandedRows[product.product_id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1, p: 1, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="subtitle1" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                          Chi tiết đơn hàng cho: {product.product_name}
                        </Typography>
                        {product.order_details && product.order_details.length > 0 ? (
                          <Table size="small" aria-label="order details">
                            <TableHead>
                              <TableRow>
                                <TableCell>Mã ĐH</TableCell>
                                <TableCell>Khách hàng</TableCell>
                                <TableCell align="right">SL trong ĐH</TableCell>
                                <TableCell align="right">Tiền trong ĐH</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {product.order_details.map((detail) => (
                                <TableRow key={detail.order_id}>
                                  <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <MuiLink component={Link} to={`/orders/${detail.order_id}`} underline="hover">
                                        {detail.order_name}
                                      </MuiLink>
                                      {detail.from_combo && (
                                        <Tooltip title={`Từ combo: ${detail.from_combo}`} arrow>
                                          <Chip
                                            label="Combo"
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ fontSize: '0.65rem', height: '20px' }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{detail.user_name}</TableCell>
                                  <TableCell align="right">{detail.quantity_in_order}</TableCell>
                                  <TableCell align="right">
                                    {detail.amount_in_order?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" sx={{ p:1}}>Không có chi tiết đơn hàng.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrderItemsReport;
